import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Checkbox,
  Avatar,
  Paper,
  Divider,
  Chip,
} from "@mui/material";
import { FileText, Search } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { axiosPrivate } from "src/common/axiosProtected";
import { addOrdersToInvoice } from "src/queries/query";

const AddOrdersToInvoiceModal = ({ open, onClose, orderId }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [error, setError] = useState("");
  const queryClient = useQueryClient();

  // Fetch invoices
  const { data: invoicesData, isLoading: isLoadingInvoices } = useQuery({
    queryKey: ["invoices-list"],
    queryFn: async () => {
      const response = await axiosPrivate.get("/api/v1/invoices");
      return response.data?.data || [];
    },
    enabled: open,
  });

  // Filter invoices based on search term
  const filteredInvoices = useMemo(() => {
    if (!invoicesData) return [];
    return invoicesData.filter(
      (invoice) =>
        invoice.invoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.customerName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [invoicesData, searchTerm]);

  // Mutation for adding orders to invoice
  const { mutate: addOrders, isPending: isAdding } = useMutation({
    mutationFn: () => addOrdersToInvoice(selectedInvoice.uuid, [orderId]),
    onSuccess: () => {
      toast.success("Order added to invoice successfully!");
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["invoices-list"] });
      handleClose();
    },
    onError: (error) => {
      const errorMessage =
        error.message || "Failed to add order to invoice. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });

  const handleClose = () => {
    setSearchTerm("");
    setSelectedInvoice(null);
    setError("");
    onClose();
  };

  const handleSelectInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setError("");
  };

  const handleAddOrder = () => {
    if (!selectedInvoice) {
      setError("Please select an invoice");
      return;
    }
    addOrders();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}
      >
        <Avatar sx={{ bgcolor: 'primary.contrastText', color: 'primary.main', width: 32, height: 32 }}>
          <FileText size={18} />
        </Avatar>
        <Typography variant="h6" component="div" sx={{ fontSize: '1.1rem' }}>
          Add Order to Invoice
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 2.5, bgcolor: 'grey.50' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              mb: 2
            }}
          >
            <Search size={18} sx={{ color: 'text.secondary', mr: 1.5 }} />
            <TextField
              fullWidth
              variant="standard"
              placeholder="Search by invoice number, order number"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              disabled={isLoadingInvoices || isAdding}
              InputProps={{
                disableUnderline: true,
              }}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: '0.9rem'
                }
              }}
            />
          </Paper>

          {isLoadingInvoices ? (
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                minHeight: 250 
              }}
            >
              <CircularProgress size={32} />
            </Box>
          ) : filteredInvoices.length === 0 ? (
            <Box 
              sx={{ 
                textAlign: 'center', 
                py: 6,
                color: 'text.secondary'
              }}
            >
              <FileText size={40} sx={{ mb: 1.5, opacity: 0.3 }} />
              <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1rem' }}>
                {searchTerm ? 'No invoices found matching your search' : 'No invoices available'}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.85rem' }}>
                {searchTerm && 'Try adjusting your search terms'}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
                Available Invoices
                <Chip 
                  label={filteredInvoices.length} 
                  size="small" 
                  sx={{ ml: 1.5, height: 20 }} 
                />
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {filteredInvoices.map((invoice) => (
                  <Paper
                    key={invoice.uuid}
                    elevation={selectedInvoice?.uuid === invoice.uuid ? 2 : 1}
                    sx={{
                      p: 1.5,
                      border: selectedInvoice?.uuid === invoice.uuid ? 2 : 1,
                      borderColor: selectedInvoice?.uuid === invoice.uuid ? 'primary.main' : 'divider',
                      borderRadius: 1.5,
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        elevation: 2,
                        borderColor: 'primary.light',
                      },
                      bgcolor: selectedInvoice?.uuid === invoice.uuid ? 'primary.50' : 'background.paper',
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                      onClick={() => handleSelectInvoice(invoice)}
                    >
                      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Checkbox
                          checked={selectedInvoice?.uuid === invoice.uuid}
                          size="small"
                        />
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, fontSize: '0.9rem', mb: 0.5 }}
                          >
                            {invoice.invoiceNumber || "N/A"}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2, mb: 0.5 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                              Order: {invoice.orderNumber || "N/A"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                              Customer: {invoice.customer?.name || "N/A"}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.75rem' }}>
                            Status: {invoice.status || "N/A"}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, bgcolor: 'grey.50' }}>
        <Button 
          onClick={handleClose} 
          disabled={isAdding}
          size="medium"
          color='error'
          sx={{ minWidth: 100 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleAddOrder}
          variant="contained"
          disabled={!selectedInvoice || isAdding}
          size="medium"
          sx={{
            minWidth: 100,
            px: 3,
            py: 0.75
          }}
        >
          {isAdding ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Add Order"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddOrdersToInvoiceModal;
